<template>
  <div class="max-w-2xl" v-if="page">
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <h1 class="font-slab text-2xl text-brand-blue">{{ page.title }}</h1>
    <LocationProblem v-if="locationError" :error="locationError" subject="rain totals near you" my-4 />
    <LocationLookup v-model="selectedLocation" />
    <div space-y-4>
      <h2 text-2xl font-semibold text-stone-500>Station Readings</h2>
      <div grid grid-flow-dense sm:grid-cols-2 gap-2 v-if="snow?.data">
        <template v-for="reading in snow.data" :key="reading.station_id">
          <div
            v-if="isNumber(reading.precipitation_inches)"
            class="rounded bg-stone-50 p-3 text-lg flex flex-col justify-between border-stone-100 border-3"
          >
            <div pb-3>
              <div text-stone-600 lh-snug font-semibold>
                {{ reading.station_name }}
              </div>
              <div flex text-stone-500 font-mono text-sm>
                <div class="after:(content-['•'] last:content-none mx-2)">
                  {{ round(reading.distance, 0) }} mi {{ reading.direction }}
                </div>
                <div v-if="readingDate(reading)" :title="readingDate(reading).toLocaleString()">
                  <UseTimeAgo v-slot="{ timeAgo }" :time="readingDate(reading)">
                    {{ timeAgo }}
                  </UseTimeAgo>
                </div>
              </div>
              <div v-if="isNumber(reading.precipitation_inches)">
                <div text-3xl font-semibold text-stone-700 tabular-nums>{{ reading.precipitation_inches }} in</div>
                <div font-mono text-stone-400 lh-snug>rain over {{ reading.precipitation_duration_hours }} hours</div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div space-y-4 v-if="snow && selectedLocation?.module === 'currentLocation'">
      <div text-stone-5 prose v-if="isLocationSupported">
        <p v-if="!loading && snow?.error && !isBot()">An error occurred. Please try again later.</p>
        <p v-else-if="!loading && !snow?.data && !isBot()">
          We could not determine the {{ page.label }} for your location.
        </p>
        <p>
          By default, the site will gather the gps coordinates directly from your device and then uses that information
          to lookup information about where you are.
        </p>
      </div>
    </div>
    <Ad ad-slot="1948746426" />
    <WikipediaInfo v-if="placeName" :slug="placeName" />
    <div class="prose">
      <h2 class="text-3xl font-semibold text-stone-500">{{ page?.subtitle || "What is this?" }}</h2>
      <ContentRenderer v-if="page" :value="page" />
    </div>
  </div>
</template>
<script setup>
import { UseTimeAgo } from "@vueuse/components";

const { selectedLocation, coordinates, placeName, locationError, setMapLocation, onCoordinatesChange, geolocation } =
  useSelectedLocation();
const { isLocationSupported, loading: locationLoading } = geolocation;

const page = await useContent("/rain");

useSeoMeta({
  title: () => {
    if (placeName.value) {
      return `${placeName.value} - ${page.value.title}`;
    }
    return `${page.value.title} - ${page.value.subtitle}`;
  },
});

defineOgImage({
  title: page.value.title,
  subtitle: page.value.subtitle,
});

const snow = ref();
const forecast = ref();

const loading = ref(true);

function readingDate(reading) {
  if (reading.precipitation_date) {
    return new Date(reading.precipitation_date);
  }
  return null;
}

watch(selectedLocation, () => {
  if (selectedLocation.value == null) {
    return;
  }
  loading.value = true;
  snow.value = null;
});

onCoordinatesChange(async () => {
  try {
    snow.value = await useFetchSnow(coordinates.value);
    forecast.value = await useFetchForecast(coordinates.value, "precipitation");
    loading.value = false;
  } catch (error) {
    console.error(error);
  }
  loading.value = false;
});

</script>
